// src/components/TeamCarousel.js
import React from 'react';
import Slider from 'react-slick';
import { Box, useTheme, useMediaQuery, Typography } from '@mui/material';
import MuiLink from '@mui/material/Link'; // <-- Import MUI Link
import TeamMemberCard from './TeamMemberCard';
import type { TeamMember } from './TeamMemberCard';
import { Link as RouterLink } from 'react-router-dom'; // <-- Import React Router Link as RouterLink

// Import slick carousel styles (ensure these are globally available or imported here)
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

interface TeamCarouselProps {
    teamMembers: TeamMember[] | null | undefined; // Expect an array of TeamMembers
}

const TeamCarousel: React.FC<TeamCarouselProps> = ({ teamMembers }) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  // No longer need useNavigate or handleMemberClick

  if (!teamMembers || teamMembers.length === 0) {
    return <Typography sx={{ textAlign: 'center', my: 4 }}>No team members to display.</Typography>;
  }

  const getSlidesToShow = () => {
    if (teamMembers.length === 1) return 1;
    if (isMd) return Math.min(3, teamMembers.length);
    if (isSm) return Math.min(2, teamMembers.length);
    return 1;
  };

  const slidesToShow = getSlidesToShow();

  const settings = {
    dots: true,
    infinite: teamMembers.length > slidesToShow,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: false,
    pauseOnHover: true,
    arrows: teamMembers.length > slidesToShow,
    adaptiveHeight: false,
    centerMode: teamMembers.length > 1 && slidesToShow > 1 && teamMembers.length >= slidesToShow,
    centerPadding: '0px',
  };

  return (
    <Box
      sx={{
        mb: { xs: 8, md: 12 },
        px: { xs: 0, sm: (teamMembers.length > 1 && slidesToShow > 1) ? 5 : 0 },
        maxWidth: '1200px',
        mx: 'auto',

        // --- Slick Carousel Styling Overrides ---
        '.slick-list': {
           margin: { xs: '0 -8px', sm: '0 -12px' },
        },
        '.slick-slide': {
          // opacity: 0,
          // transition: 'opacity 0.5s ease-in-out',
        },
        '.slick-slide.slick-active': {
          // opacity: 1,
        },
        '.slick-slide > div': { // Target the div slick adds for slide padding
          margin: { xs: '0 8px', sm: '0 12px' }, // Control spacing between slides
        },
        '.slick-track': {
            display: 'flex', // Ensure flex alignment
            alignItems: 'stretch', // Make slides stretch to the same height
        },
        '.slick-dots': {
          bottom: '-30px',
          'li button:before': {
            fontSize: '10px',
            color: 'primary.main',
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
          },
          'li.slick-active button:before': {
            opacity: 1,
          },
        },
        '.slick-arrow': {
           '&:before': {
            // color: 'primary.main',
            // fontSize: '24px',
           }
        },
        ...(slidesToShow === 1 && teamMembers.length === 1 && {
             '.slick-arrow': { display: 'none !important' }
        })
      }}
    >
      <Slider {...settings}>
        {teamMembers.map((member, index) => (
          // --- WRAP WITH MUI LINK ---
          <MuiLink
            component={RouterLink}             // Use React Router for navigation
            to={`/members/${member.id}/`}      // Set the link destination
            key={member.id || index}           // Add a unique key
            underline="none"                   // <-- THE FIX: Remove underline
            color="inherit"                    // Inherit text color
            onClick={() => window.scrollTo(0, 0)} // Keep scroll behavior
            sx={{                              // Use sx for styling
                display: 'block',              // Ensure it takes up space
                height: '100%',                // Ensure it takes up space
                cursor: 'pointer',             // Keep the hand cursor
                '&:hover': {                   // Ensure no underline on hover
                    textDecoration: 'none',
                }
            }}
          >
            {/* Inner Box and Card remain the same */}
            <Box
              sx={{
                height: '100%',
                display: 'flex',
              }}
            >
              <TeamMemberCard member={member} />
            </Box>
          </MuiLink>
          // --- END WRAP ---
        ))}
      </Slider>
    </Box>
  );
};

export default TeamCarousel;